import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Layout,
  Typography,
  Form,
  Input,
  Select,
  Button,
  Table,
  Card,
  Row,
  Col,
  Space,
  Tag,
  theme,
  Breadcrumb,
} from "antd";
import axios from "axios";
import Headerdiv from "../layout/header.js";
import Footerdiv from "../layout/footer.js";
import { MAINAPIURL, APPBACKGROUND_COLOR } from "../appsettings.js";
import { AlertError, AlertSuccess } from "../usableclasses.js";

const { Option } = Select;
const { Content } = Layout;

const PurchaseOrderPage = () => {
  const navigate = useNavigate();
  const ApiURL = MAINAPIURL;
  const TOKEN = localStorage.getItem("AUTHCODE");
  const { token: { colorBgContainer, borderRadiusLG } } = theme.useToken();

  const [form] = Form.useForm();

  const [searchParams, setSearchParams] = useState({
    keyword: "",
    financialYear: "",
    year: "",
    month: "",
    vendor: "",
    status: "PENDING",
    sortField: "",
    sortMethod: "",
  });

  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [loadingFinancialYears, setLoadingFinancialYears] = useState(false);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 5;

  // Generate year list (memoized)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString());
    setYearList(years);
  }, []);

  // Fetch data with stable callbacks
  const fetchFinancialYears = useCallback(async () => {
    setLoadingFinancialYears(true);
    try {
      const res = await axios.get(`${ApiURL}/master/financeyear/dropdown`, {
        headers: { SystemAccessToken: TOKEN },
      });
      setFinancialYears(Array.isArray(res.data) ? res.data : []);
    } catch {
      AlertError("Failed to load financial years");
      setFinancialYears([]);
    } finally {
      setLoadingFinancialYears(false);
    }
  }, [ApiURL, TOKEN]);

  const fetchVendors = useCallback(async () => {
    setLoadingVendors(true);
    try {
      const res = await axios.get(`${ApiURL}/vendor/dropdown`, {
        headers: { SystemAccessToken: TOKEN },
      });
      setVendors(Array.isArray(res.data) ? res.data.filter(v => v && v.label) : []);
    } catch {
      AlertError("Failed to load vendors");
      setVendors([]);
    } finally {
      setLoadingVendors(false);
    }
  }, [ApiURL, TOKEN]);

  useEffect(() => {
    fetchFinancialYears();
    fetchVendors();
  }, [fetchFinancialYears, fetchVendors]);

  // Status badge (memoized)
  const getStatusBadge = useCallback((status) => {
    switch ((status || "").toUpperCase()) {
      case "DRAFT":
        return <Tag color="orange">Draft</Tag>;
      case "PENDING":
        return <Tag color="blue">Pending</Tag>;
      case "APPROVED":
        return <Tag color="green">Approved</Tag>;
      case "REJECTED":
        return <Tag color="red">Rejected</Tag>;
      case "DELETED":
        return <Tag color="red">Deleted</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  }, []);

  const handleFilter = useCallback(
    async (values, pageNum = 1) => {
      setLoading(true);
      try {
        const params = {
          keyword: values.keyword || "",
          finYear: values.financialYear || "",
          sysYear: values.year || "",
          sysMonth: values.month || "",
          vendorId: values.vendor || "",
          currentStatus: values.status || "PENDING",
          pageLength: rowsPerPage,
          pageNumber: pageNum,
          orderColumn: values.sortField || "",
          orderBy: values.sortMethod || "",
        };

        const res = await axios.post(`${ApiURL}/purchase/polistfilter`, params, {
          headers: { SystemAccessToken: TOKEN },
        });

        const data =
          res.data?.dataList ||
          res.data?.records ||
          res.data?.data ||
          (Array.isArray(res.data) ? res.data : []);

        const processed = (data || []).map((r) => ({
          poId: r.poId || "",
          poReferance: r.poReferance || "",
          createDate: r.createDate || "",
          vendorName: r.vendorName || "",
          totalCost: r.totalCost || "0.00",
          sysYear: r.sysYear || "",
          sysMonth: r.sysMonth || "",
          vendorBillNo: r.vendorBillNo || "",
          currentStage: r.currentStage || "",
        }));

        setFilteredData(processed);
        setTotalRecords(res.data?.rowcount || processed.length);
        setCurrentPage(pageNum);
        setSearchParams(values);
      } catch (e) {
        AlertError(`Failed to load purchase orders: ${e.message}`);
      } finally {
        setLoading(false);
      }
    },
    [ApiURL, TOKEN]
  );

  // Initial load
  useEffect(() => {
    handleFilter(searchParams, 1);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = useCallback(() => {
    form.resetFields();
    const defaults = {
      keyword: "",
      financialYear: "",
      year: "",
      month: "",
      vendor: "",
      status: "PENDING",
      sortField: "",
      sortMethod: "",
    };
    setSearchParams(defaults);
    handleFilter(defaults, 1);
  }, [form, handleFilter]);

  const handlePageChange = (page) => {
    form.validateFields().then((values) => handleFilter(values, page));
  };

  const handleViewPO = (record) => navigate(`/poview/${record.poId}`);

  const columns = useMemo(
    () => [
      { title: "PO Reference", dataIndex: "poReferance", key: "poReferance" },
      {
        title: "Create Date",
        dataIndex: "createDate",
        render: formatDate,
      },
      {
        title: "Status",
        dataIndex: "currentStage",
        render: getStatusBadge,
      },
      {
        title: "PO Value",
        dataIndex: "totalCost",
        render: (v) => parseFloat(v).toLocaleString(),
      },
      { title: "Vendor", dataIndex: "vendorName" },
      { title: "Year", dataIndex: "sysYear" },
      {
        title: "Month",
        dataIndex: "sysMonth",
        render: (m) =>
          ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
            parseInt(m) - 1
          ] || m,
      },
      { title: "Vendor Bill No", dataIndex: "vendorBillNo" },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space size="small">
            <Button type="primary" size="small" onClick={() => handleViewPO(record)}>
              View
            </Button>
          </Space>
        ),
      },
    ],
    [formatDate, getStatusBadge]
  );

  return (
    <Layout style={{ backgroundColor: APPBACKGROUND_COLOR }}>
      <Headerdiv />
      <Content style={{ padding: "10px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item>
            <Link to="/polist">Purchase Orders</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>Approval Pending List</Breadcrumb.Item>
        </Breadcrumb>

        <div
          style={{
            padding: 20,
            minHeight: "65vh",
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Space direction="vertical" style={{ width: "100%", padding: "15px" }}>
            <Card title="Search Purchase Orders" style={{ marginBottom: 16 }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFilter}
                initialValues={searchParams}
              >
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={12} md={4}>
                    <Form.Item name="keyword" label="Search Keyword">
                      <Input placeholder="Search..." />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Form.Item name="financialYear" label="Financial Year">
                      <Select placeholder="Select" allowClear loading={loadingFinancialYears}>
                        {financialYears.map((y) => (
                          <Option key={y.id} value={y.name}>
                            {y.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Form.Item name="year" label="Year">
                      <Select placeholder="Select" allowClear>
                        {yearList.map((y) => (
                          <Option key={y} value={y}>
                            {y}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Form.Item name="month" label="Month">
                      <Select placeholder="Select" allowClear>
                        {Array.from({ length: 12 }, (_, i) => (
                          <Option key={i + 1} value={(i + 1).toString().padStart(2, "0")}>
                            {new Date(0, i).toLocaleString("default", { month: "long" })}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item name="vendor" label="Vendor">
                      <Select placeholder="Select" allowClear loading={loadingVendors}>
                        {vendors.map((v) => (
                          <Option key={v.id} value={v.id}>
                            {v.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 8]} align="bottom">
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="sortField" label="Sort By">
                      <Select placeholder="Select Field" allowClear>
                        <Option value="REFERANCE">PO Reference</Option>
                        <Option value="CREATED_DATE">Create Date</Option>
                        <Option value="FINANCE_YEAR">Finance Year</Option>
                        <Option value="COST">Total Cost</Option>
                        <Option value="PAID_AMOUNT">Paid Amount</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item name="sortMethod" label="Sort Order">
                      <Select placeholder="Select Order" allowClear>
                        <Option value="ASC">Ascending</Option>
                        <Option value="DESC">Descending</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} className="text-right">
                    <Form.Item>
                      <Space>
                        <Button type="primary" htmlType="submit">
                          Filter
                        </Button>
                        <Button onClick={handleClear}>Clear</Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>

            <Card title="Purchase Order List">
              <Table
                columns={columns}
                dataSource={filteredData}
                pagination={{
                  current: currentPage,
                  pageSize: rowsPerPage,
                  total: totalRecords,
                  onChange: handlePageChange,
                  showSizeChanger: false,
                  showTotal: (t, r) => `Showing ${r[0]}-${r[1]} of ${t}`,
                }}
                rowKey="poId"
                loading={loading}
                scroll={{ x: true }}
                locale={{
                  emptyText: loading ? "Loading..." : "No data found",
                }}
              />
            </Card>
          </Space>
        </div>
      </Content>
      <Footerdiv />
    </Layout>
  );
};

export default PurchaseOrderPage;
